/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState, useContext } from 'react';
import { StyledRoomsAndGuestDiv } from './index.styles';
import Row from '../../atoms/roomsAndGuestsRow';
import { ChildrenAgeCollectionType, RoomsAndGuestsProps } from './index.types';
import Popup from '../../atoms/popup/popup';
import PopupMain from '../../atoms/popupMain/popupMain';
import PopupHeader from '../../atoms/popupHeader/popupHeader';
import { useConditionalPageContextSERP } from '../../../../../../hooks';
import { setRoomsAndGuests } from '../../../store/store.actions';
import { ROOMS_AND_GUESTS, UNIT_OF_ROOM } from '../../../constants/StoreConstants';
import PopupFooter from '../../atoms/popupFooter/popupFooter';
import { CustomSlider } from '@marriott/mi-ui-library-shop';
import { Button } from '@marriott/mi-ui-library';
import CommonUtils from '../../../utils/CommonUtils';
import clsx from 'clsx';
import { useCheckBreakpoint } from '@marriott/mi-ui-library';
import { transformGuestText, transformRoomText } from '../../../../../../utils/src/utils';
import {
  checkIfValidRoomCountValue,
  getRoomsCountDisplayValue,
} from '../../../../../../modules/utils/searchFormHelper';
import { ContentContext, ViewportContext, FocusContext, FormValuesContext } from '../../index.contexts';

declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface HTMLAttributes<T> {
    custom_click_track_value?: string;
  }
}

const RoomsAndGuests: React.FC<RoomsAndGuestsProps> = ({
  onCancel,
  onChange: changeMobileState,
  isPublishUserSelection = false,
  isSearchPage = false,
  isADFPage = false,
}) => {
  const compRef = useRef<HTMLDivElement>(null);
  const isMobileView = useContext(ViewportContext);
  const LESS_THAN_LABEL = '<1';
  const {
    maxChildAge,
    maxGuestPerRoom,
    roomListItems,
    roomsGuestsHead,
    rooms,
    room,
    guests,
    guest,
    adults,
    children,
    ageBasedRatesLabel,
    childAgeRequired,
    reset,
    lessThanLabel,
    cancel,
    ageLabel,
    applyLabel,
    done,
    roomsGuestsHeadShortLabel,
    roomShortLabel,
    searchGuestsShortLabel,
    searchGuestShortLabel,
    roomsShortLabel,
    unitOfRoom = '',
  } = useContext(ContentContext);
  const { setIsUserInteractWithComp } = useContext(FocusContext);
  const { currentLocale } = useConditionalPageContextSERP();
  const [popupOpenState, setPopupOpenState] = React.useState(false);
  const clickTrackingLoc = 'Search Form';
  const isDesktopView = useCheckBreakpoint('viewportL');

  const arrayInitializer = (length: number, key: string, incrementValue = 0): Array<Record<string, string>> =>
    Array.from({ length }, (_, i) => {
      return { [key]: `${i + incrementValue}` };
    });

  const roomsList = roomListItems as Array<{ roomQuantity: string }>;
  const roomsMinValue: { roomQuantity: string } = { ...roomsList?.[0] };
  const roomsMaxValue: { roomQuantity: string } = { ...roomsList?.[roomsList.length - 1] };

  const adultsList = arrayInitializer(+(maxGuestPerRoom as string), 'adultsQuantity', 1);
  const adultsMinValue = { ...adultsList[0] };

  const childrenList = arrayInitializer(+(maxGuestPerRoom as string), 'childrenQuantity');
  const childrenMinValue = { ...childrenList[0] };

  const childAgeList = arrayInitializer(+(maxChildAge as string) + 1, 'val', 0);
  childAgeList[0] = { val: isSearchPage ? '0' : lessThanLabel.toLowerCase() };
  const childrenAgeMinValue = { ...childAgeList[0] };
  const childrenAgeMaxValue = { ...childAgeList[childAgeList.length - 1] };
  const totalGuests = +(maxGuestPerRoom as string);

  // get the initial rooms and guests values by checking the formValues
  const { formValues, setFormValues, activePopup, setActivePopup } = useContext(FormValuesContext);
  // const { interactionsSelected, setInteractionsSelected } = useSessionUpdateContext();
  const [inputHasFocused, setInputHasFocused] = useState<Array<boolean | undefined>>(
    Array.from({ length: parseInt(maxChildAge) }, () => false)
  );

  const roomsGuestsSavedData = formValues?.[ROOMS_AND_GUESTS];
  const roomCount = roomsGuestsSavedData?.roomsCount || '1';
  const roomsCountSaved = isADFPage ? getRoomsCountDisplayValue(parseInt(roomCount)) : roomCount;
  const adultsCountSaved = roomsGuestsSavedData?.adultsCount;
  const childrenCountSaved = roomsGuestsSavedData?.childrenCount;
  const childrenAgesSaved = roomsGuestsSavedData?.childrenAges;

  // sometimes childrenAgesSaved is coming as "" causing component to break
  const childrenAgesCollectionSaved =
    typeof childrenAgesSaved === 'object'
      ? childrenAgesSaved?.map((val: string, index: any) => {
          const value = isMobileView && val !== '0' ? val : childAgeList[parseInt(val, 10)]?.['val'];
          return { id: index, value, isValueSet: value !== '0' };
        })
      : childrenAgesSaved?.split(',')?.map((val: string, index: any) => {
          const value = isMobileView && val !== '0' ? val : childAgeList[parseInt(val, 10)]?.['val'];
          return { id: index, value, isValueSet: value !== '0' };
        });

  const [roomsCount, setRoomsCount] = React.useState<Record<string, string>>(
    roomsCountSaved ? { roomQuantity: roomsCountSaved.toString() } : roomsMinValue
  );
  const [adultsCount, setAdultsCount] = React.useState<Record<string, string>>(
    adultsCountSaved ? { adultsQuantity: adultsCountSaved.toString() } : adultsMinValue
  );
  const [childrenCount, setChildrenCount] = React.useState<Record<string, string>>(
    childrenCountSaved ? { childrenQuantity: childrenCountSaved.toString() } : childrenMinValue
  );
  const [childrenAgeCollection, setChildrenAgeCollection] = React.useState<Array<ChildrenAgeCollectionType>>(
    childrenAgesCollectionSaved ? childrenAgesCollectionSaved : []
  );

  useEffect(() => {
    if (popupOpenState) {
      setFocusOnFirstBtn();
    } else {
      if (!isMobileView) {
        applyBtnHandler();
      }
      // Clear activePopup when popup closes
      setActivePopup?.(null);
    }

    if (isPublishUserSelection && !isMobileView) {
      applyBtnHandler();
    }
  }, [popupOpenState, isMobileView, isPublishUserSelection, setActivePopup]);

  useEffect(() => {
    // To focus the first interactive element
    if (isMobileView) {
      CommonUtils.focusFirstTabbable(compRef);
    }
  }, []);

  useEffect(() => {
    // when rooms and guests are changed update the values in formValues
    setFormValues(
      setRoomsAndGuests({
        ...(formValues?.[ROOMS_AND_GUESTS] ?? {}),
        roomsCount: roomsCount['roomQuantity'],
        adultsCount: parseInt(adultsCount['adultsQuantity'], 10),
        childrenCount: parseInt(childrenCount['childrenQuantity'], 10),
        childrenAges: childrenAgeCollection?.map(obj => obj.value),
      })
    );
  }, [roomsCount, adultsCount, childrenCount, childrenAgeCollection]);

  const setFocusOnFirstBtn = (): void => {
    const popupElemBtn = compRef.current?.querySelector('.rg-popup button') as HTMLElement;
    popupElemBtn?.focus();
  };

  //To update the children collection based on children row
  const updateChildrenCollection = (count: number): void => {
    if (childrenAgeCollection.length !== count) {
      const childrenAgeCollectionClone: Array<ChildrenAgeCollectionType> = childrenAgeCollection?.map(element => {
        return { ...element };
      });

      //To handle decrement button click
      if (childrenAgeCollectionClone.length > count) {
        childrenAgeCollectionClone.pop();
      }
      //To handle increment button click
      if (childrenAgeCollectionClone.length < count) {
        const childToAdd = {
          id: 0,
          value: '0', // set default value to -1 before user interaction
          isValueSet: false, // set this to true on first user interaction
        };
        if (childrenAgeCollectionClone.length > 0) {
          childToAdd.id = childrenAgeCollectionClone[childrenAgeCollectionClone.length - 1].id + 1;
        }
        childrenAgeCollectionClone.push(childToAdd);
      }
      setChildrenAgeCollection(childrenAgeCollectionClone);
    }
  };

  //To reset the values in all the rows
  const resetHandler = (e?: React.MouseEvent): void => {
    e?.preventDefault();
    setFocusOnFirstBtn();
    setRoomsCount(roomsMinValue);
    setAdultsCount(adultsMinValue);
    setChildrenCount(childrenMinValue);
    setChildrenAgeCollection([]);
  };

  //To handle rooms state count change
  const roomsChangeHandler = (type: string): void => {
    const i = roomsList.findIndex(ele => ele.roomQuantity === roomsCount['roomQuantity']);
    if (i === -1) {
      setRoomsCount({ ...roomsList[0] });
    } else {
      if (type === 'inc') {
        setRoomsCount({ ...roomsList[i + 1] });
      } else {
        setRoomsCount({ ...roomsList[i - 1] });
      }
    }
  };

  //To handle adults state count change
  const adultsChangeHandler = (type: string): void => {
    const i = adultsList.findIndex(ele => ele['adultsQuantity'] === adultsCount['adultsQuantity']);
    if (type === 'inc') {
      setAdultsCount({ ...adultsList[i + 1] });
    } else {
      setAdultsCount({ ...adultsList[i - 1] });
    }
  };

  //To handle children state count change
  const childrenChangeHandler = (type: string): void => {
    const i = childrenList.findIndex(ele => ele['childrenQuantity'] === childrenCount['childrenQuantity']);
    if (type === 'inc') {
      setChildrenCount({ ...childrenList[i + 1] });
      updateChildrenCollection(parseInt(childrenList[i + 1]['childrenQuantity'], 10));
    } else {
      setChildrenCount({ ...childrenList[i - 1] });
      updateChildrenCollection(parseInt(childrenList[i - 1]['childrenQuantity'], 10));
    }
  };

  //To handle each child state count change
  const childAgeChangeHandler = (index: number, type?: string, age?: number): void => {
    const childrenAgeCollectionClone: Array<ChildrenAgeCollectionType> = childrenAgeCollection?.map(element => {
      return { ...element };
    });
    // if value is not already set then set the value to minimum age value
    if (!childrenAgeCollectionClone[index].isValueSet) {
      childrenAgeCollectionClone[index].isValueSet = true;
      childrenAgeCollectionClone[index].value = childrenAgeMinValue['val'];
    } else {
      const i = childAgeList.findIndex(ele => ele['val'] === childrenAgeCollectionClone[index].value);
      if (type) {
        if (type === 'inc') {
          childrenAgeCollectionClone[index].value = childAgeList[i + 1]['val'];
        } else {
          childrenAgeCollectionClone[index].value = childAgeList[i - 1]['val'];
        }
      }
    }
    if (typeof age !== 'undefined') {
      // store child's age in the clone array
      childrenAgeCollectionClone[index].value = childAgeList[age]['val'];
    }
    // update the chidlren age collection
    setChildrenAgeCollection(childrenAgeCollectionClone);
  };

  // To handle adults and following rows hide
  const checkHideRows = (): boolean => {
    if (roomsList?.findIndex(ele => ele.roomQuantity === roomsCount['roomQuantity']) >= 4) {
      return false;
    }
    return true;
  };

  // to show popup on desktop on button click
  const showPopup = (): void => {
    setPopupOpenState(true);
    setIsUserInteractWithComp(true);
    setActivePopup?.('roomsAndGuests');
  };

  // Close popup when another popup becomes active
  useEffect(() => {
    if (activePopup !== null && activePopup !== 'roomsAndGuests' && popupOpenState) {
      setPopupOpenState(false);
    }
  }, [activePopup]);

  /**
   * returns the string format of rooms and guest count for display
   * @param roomsCount
   * @param adultsCount
   * @param childrenCount
   */
  const getRoomsAndGuestCount = (roomsCount: string, adultsCount: number, childrenCount: number): string => {
    const guestCount = adultsCount + childrenCount;
    const guestText =
      isMobileView || (isADFPage && isDesktopView)
        ? guestCount > 1
          ? guests
          : guest
        : guestCount > 1
        ? searchGuestsShortLabel
        : searchGuestShortLabel;
    const roomsText =
      isMobileView || (isADFPage && isDesktopView)
        ? roomsCount?.toString() !== '1'
          ? rooms
          : room
        : roomsCount?.toString() !== '1'
        ? roomsShortLabel
        : roomShortLabel;
    // CNWEB-2651 `unitOfRoom` is only applicable for certain locales like CN
    // For CN pages where it's not applicable, it returns the constant `UNIT_OF_ROOM`
    const unit = unitOfRoom !== UNIT_OF_ROOM ? unitOfRoom : '';
    const roomCountDisplayValue = getRoomsCountDisplayValue(parseInt(roomsCount));
    const roomsDisplayValue = roomsText?.includes('{x}')
      ? roomsText?.replace('{x}', roomCountDisplayValue?.toString())
      : roomCountDisplayValue + ' ' + unit + transformRoomText(roomsText, currentLocale);

    const guestsDisplayValue = guestText?.includes('{x}')
      ? guestText?.replace('{x}', guestCount?.toString())
      : guestCount + ' ' + transformGuestText(guestText, currentLocale);
    if (checkHideRows()) {
      return `${roomsDisplayValue}, ${guestsDisplayValue}`;
    } else {
      return `${roomsDisplayValue}`;
    }
  };

  // To check total guests count (adults + children)
  const totalGuestsCheck = (): boolean => {
    return parseInt(adultsCount['adultsQuantity'], 10) + parseInt(childrenCount['childrenQuantity'], 10) >= totalGuests;
  };

  // To hide reset handler when initial values are set
  const resetHideCheck = (): boolean => {
    return (
      roomsCount['roomQuantity'] !== roomsMinValue.roomQuantity ||
      adultsCount['adultsQuantity'] !== adultsMinValue['adultsQuantity'] ||
      childrenCount['childrenQuantity'] !== childrenMinValue['childrenQuantity']
    );
  };

  const applyBtnHandler = (): void => {
    // let selected = interactionsSelected;
    // if (String(formValues?.roomsAndGuests?.roomsCount) !== roomsCount['roomQuantity']) {
    //   selected = { ...selected, rooms: true };
    //   setInteractionsSelected(selected);
    // }
    // if (formValues?.roomsAndGuests?.adultsCount !== parseInt(adultsCount['adultsQuantity'], 10)) {
    //   selected = { ...selected, adults: true };
    //   setInteractionsSelected(selected);
    // }
    // if (
    //   formValues?.roomsAndGuests?.childrenCount !== parseInt(childrenCount['childrenQuantity'], 10) ||
    //   (childrenAgeCollection.length &&
    //     formValues?.roomsAndGuests?.childrenAges !== childrenAgeCollection.map(obj => obj.value))
    // ) {
    //   selected = { ...selected, children: true };
    //   setInteractionsSelected(selected);
    // }
    // when Apply button is clicked, update the FormValues accordingly
    setFormValues(
      setRoomsAndGuests({
        ...(formValues?.[ROOMS_AND_GUESTS] ?? {}),
        roomsCount: roomsCount['roomQuantity'],
        adultsCount: parseInt(adultsCount['adultsQuantity'], 10),
        childrenCount: parseInt(childrenCount['childrenQuantity'], 10),
        childrenAges: childrenAgeCollection?.map(obj => obj.value),
      })
    );
    // when Apply button is clicked, update mobile state
    if (changeMobileState) {
      changeMobileState();
    }
  };

  const formatChildAge = (childAgeRequired: string, index: number) => {
    return childAgeRequired.replace('{x}', (index + 1).toString());
  };

  const roomsGuestsLabel = getRoomsAndGuestCount(
    roomsCount['roomQuantity'],
    +adultsCount['adultsQuantity'],
    +childrenCount['childrenQuantity']
  );

  return (
    <StyledRoomsAndGuestDiv
      ref={compRef}
      className={clsx(
        'standard',
        'browser-outline',
        isSearchPage && !isMobileView && 'search-rooms',
        isSearchPage && isMobileView && 'search-child-rooms-form',
        isADFPage && 'adf-search-rooms'
      )}
    >
      {isMobileView
        ? ''
        : !isSearchPage && (
            <button type="button" className="search__rates t-font-s" onClick={showPopup} aria-expanded={popupOpenState}>
              {getRoomsAndGuestCount(
                roomsCount['roomQuantity'],
                +adultsCount['adultsQuantity'],
                +childrenCount['childrenQuantity']
              )}
              <span className="icon-dropdown-down"></span>
            </button>
          )}
      {/* flag added to show popup incase of ADF Page */}

      {(isSearchPage || isMobileView || popupOpenState || isADFPage) && (
        <Popup
          className={`rg-popup custom-scrollbar   ${
            popupOpenState ? `${isADFPage ? 'open-state' : ''}` : 'close-popup-header'
          } ${isADFPage && 'adf-rooms-popup'}`}
          show={true}
          handleBlur={!isMobileView}
          popupOpenState={popupOpenState}
          setPopupOpenState={setPopupOpenState}
          role={!isMobileView ? 'dialog' : ''}
          labelledBy="rooms_and_guests"
        >
          {!isMobileView && (isSearchPage || isADFPage) ? (
            <PopupHeader
              cancelLabel={cancel}
              cancelClickHandler={onCancel}
              mainText={getRoomsAndGuestCount(
                roomsCount['roomQuantity'],
                +adultsCount['adultsQuantity'],
                +childrenCount['childrenQuantity']
              )}
              labelText={
                (isMobileView || (isADFPage && isDesktopView) ? roomsGuestsHead : roomsGuestsHeadShortLabel) || ''
              }
            >
              {!popupOpenState && (
                <button
                  type="button"
                  className={`search__rates t-font-s search-button custom_click_track ${
                    isADFPage && 't-subtitle-xl adf-btn'
                  }`}
                  onClick={showPopup}
                  aria-expanded={popupOpenState}
                  custom_click_track_value={`${clickTrackingLoc}| Rooms & Guests Field |internal`}
                  id="rooms_and_guests_section"
                >
                  {roomsGuestsLabel}
                  {/* flag added to hide icons incase of ADF Page */}
                  {!isADFPage && <span className="icon-dropdown-down"></span>}
                </button>
              )}
            </PopupHeader>
          ) : (
            isMobileView && (
              <PopupHeader
                cancelLabel={cancel}
                cancelClickHandler={onCancel}
                mainText={getRoomsAndGuestCount(
                  roomsCount['roomQuantity'],
                  +adultsCount['adultsQuantity'],
                  +childrenCount['childrenQuantity']
                )}
                labelText={roomsGuestsHead}
              />
            )
          )}

          {(popupOpenState || isMobileView) && (
            <PopupMain className="rg-main">
              <Row
                title={rooms?.includes('{x}') ? rooms?.split('{x}')[0].trim() : rooms}
                showCurrentValue={true}
                currentValue={getRoomsCountDisplayValue(parseInt(roomsCount['roomQuantity']))}
                incrementHandler={(): void => roomsChangeHandler('inc')}
                decrementHandler={(): void => roomsChangeHandler('dec')}
                incrementDisable={
                  roomsMaxValue.roomQuantity === roomsCount['roomQuantity'] ||
                  !checkIfValidRoomCountValue(roomsList, roomsCount['roomQuantity'])
                }
                decrementDisable={roomsMinValue.roomQuantity === roomsCount['roomQuantity']}
                clickTrackingLoc={clickTrackingLoc}
                clickTrackValue={'Rooms'}
              />
              {checkHideRows() ? (
                <>
                  <Row
                    title={adults}
                    showCurrentValue={true}
                    currentValue={adultsCount['adultsQuantity']}
                    incrementHandler={(): void => adultsChangeHandler('inc')}
                    decrementHandler={(): void => adultsChangeHandler('dec')}
                    incrementDisable={totalGuestsCheck()}
                    decrementDisable={adultsMinValue['adultsQuantity'] === adultsCount['adultsQuantity']}
                    clickTrackingLoc={clickTrackingLoc}
                    clickTrackValue={'Adults'}
                  />
                  <Row
                    title={children}
                    showCurrentValue={true}
                    currentValue={childrenCount['childrenQuantity']}
                    incrementHandler={(): void => childrenChangeHandler('inc')}
                    decrementHandler={(): void => childrenChangeHandler('dec')}
                    incrementDisable={totalGuestsCheck()}
                    decrementDisable={childrenMinValue['childrenQuantity'] === childrenCount['childrenQuantity']}
                    clickTrackingLoc={clickTrackingLoc}
                    clickTrackValue={'Children'}
                  />
                  {childrenAgeCollection.length > 0 ? (
                    <div className="children-age-heading t-font-s search-child-label">
                      {isSearchPage ? ageBasedRatesLabel : `(${ageBasedRatesLabel})`}
                    </div>
                  ) : (
                    ''
                  )}
                  {childrenAgeCollection?.map((element: ChildrenAgeCollectionType, index: number) => (
                    <div
                      key={element.id}
                      className={
                        isSearchPage
                          ? 'search-rooms-row children-age-row'
                          : isSearchPage && isMobileView
                          ? 'children-age-row search-mobile-form-child-row'
                          : 'children-age-row'
                      }
                    >
                      {!isMobileView && !isSearchPage ? (
                        <>
                          <span>{childAgeRequired.replace('{x}', (index + 1).toString())}</span>
                          <Row
                            title={element.isValueSet ? `${element.value}` : ageLabel}
                            showCurrentValue={false}
                            childrenFlag={true}
                            currentValue={element.value}
                            incrementHandler={(): void => childAgeChangeHandler(index, 'inc')}
                            decrementHandler={(): void => childAgeChangeHandler(index, 'dec')}
                            incrementDisable={childrenAgeMaxValue['val'] === element.value}
                            decrementDisable={!element.isValueSet || childrenAgeMinValue['val'] === element.value}
                            clickTrackingLoc={clickTrackingLoc}
                            clickTrackValue={`Children ${index + 1} Age`}
                          />
                        </>
                      ) : (
                        <div className={isSearchPage ? 'child-slider-container' : ''}>
                          {isSearchPage ? (
                            <span className="flex-input-container">
                              <span className="t-label-xs search-child-label">
                                {formatChildAge(childAgeRequired, index)}
                              </span>
                              <div className="m-form-group child-age-input">
                                <label htmlFor={`age_input_${index}`} className="visually-hidden">
                                  {formatChildAge(childAgeRequired, index)}
                                </label>
                                <input
                                  type="text"
                                  pattern="[0-18]*"
                                  className="input-control t-label-xs custom_click_track"
                                  onChange={(ev: React.ChangeEvent<HTMLInputElement>): void => {
                                    childAgeChangeHandler(index, undefined, parseInt(ev.target.value, 10) || 0);
                                  }}
                                  onFocus={() => {
                                    const arr = [...inputHasFocused];
                                    arr[index] = true;
                                    setInputHasFocused(arr);
                                    if (element.value === '0') {
                                      childAgeChangeHandler(index, undefined, 0);
                                    }
                                  }}
                                  onBlur={() => {
                                    const arr = [...inputHasFocused];
                                    arr[index] = false;
                                    setInputHasFocused(arr);
                                    if (element.value === '0') {
                                      childAgeChangeHandler(index, undefined, 0);
                                    }
                                  }}
                                  name="children age input"
                                  id={`age_input_${index}`}
                                  data-testid={`age_input_slider__${index}`}
                                  value={element.isValueSet ? (element.value === '0' ? '' : element.value) : ''}
                                  {...{
                                    custom_click_track_value: `${clickTrackingLoc}| Rooms & Guests, children ${
                                      index + 1
                                    } age ${parseInt(element.value, 10) || 0} |internal`,
                                  }}
                                />
                                {element.value === '0' && !inputHasFocused[index] ? (
                                  <span className="position-absolute child-age-input-val">{LESS_THAN_LABEL}</span>
                                ) : (
                                  <span></span>
                                )}
                              </div>
                            </span>
                          ) : (
                            <span className={isSearchPage ? 't-label-s search-form-label' : 't-label-s'}>
                              {childAgeRequired.replace('{x}', (index + 1).toString()) +
                                ' ' +
                                (element.isValueSet ? `${element.value}` : ageLabel)}
                            </span>
                          )}
                          <CustomSlider
                            minValue={0}
                            maxValue={childAgeList.length - 1}
                            minLabel={
                              isSearchPage
                                ? LESS_THAN_LABEL
                                : element.isValueSet
                                ? childrenAgeMinValue['val']
                                : ageLabel
                            }
                            ariaValuetext={`${parseInt(element.value, 10) || 0}`}
                            maxLabel={childrenAgeMaxValue['val']}
                            value={parseInt(element.value, 10) || 0}
                            valueChangeHandler={(value: number): void => childAgeChangeHandler(index, undefined, value)}
                            ariaLabel={`children ${index + 1} age input`}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </>
              ) : null}
              {resetHideCheck() && popupOpenState && isSearchPage && !isMobileView && (
                <div className="reset-button-container">
                  <Button
                    buttonCopy={reset}
                    callback={() => resetHandler()}
                    className={clsx(
                      Number(childrenCount['childrenQuantity']) > 1 ? 'addBtmMargin' : '',
                      't-label-s',
                      'button-primary',
                      'button--medium'
                    )}
                    custom_click_track_value={`${clickTrackingLoc}|Rooms & Guests, Reset Button|internal`}
                  />
                </div>
              )}
            </PopupMain>
          )}
          {isMobileView || popupOpenState ? (
            resetHideCheck() ? (
              <PopupFooter
                primaryBtnLabel={isSearchPage ? done : applyLabel}
                resetLabel={reset}
                resetClickHandler={resetHandler}
                primaryBtnClickHandler={applyBtnHandler}
              />
            ) : (
              <PopupFooter
                primaryBtnLabel={isSearchPage ? done : applyLabel}
                primaryBtnClickHandler={applyBtnHandler}
              />
            )
          ) : resetHideCheck() && popupOpenState ? (
            <Button
              buttonCopy={reset}
              callback={() => resetHandler()}
              className={clsx(
                Number(childrenCount['childrenQuantity']) > 1 ? 'addBtmMargin' : '',
                't-label-s',
                'button-primary',
                'button--medium'
              )}
              {...{ custom_click_track_value: `${clickTrackingLoc}|  Rooms & Guests Reset Button |internal` }}
            />
          ) : null}
        </Popup>
      )}
    </StyledRoomsAndGuestDiv>
  );
};

export default RoomsAndGuests;
